import useFetchQuery from '@/hooks/useFetchQuery';
import { usePageMetadata } from '@/hooks';
import { useCampaignsQuery } from '@/services/revenueApi';
import type { CampaignsApiResponse, CampaignsFilter } from '@/types';
import { useCallback, useMemo, useState } from 'react';
import { CardContent, CardShellEcho } from '../common';
import TableFiltersEcho from './TableFiltersEcho';
import CampaignsTableEcho from '@/components/tables/CampaignsTable/CampaignsTableEcho';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';
import { MIXPANEL_EVENT, RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';
import type { PageType } from '@/types';

type FilterType = 'show' | 'advertiser' | 'campaign_type';

const getCampaignsFilterEventNames = (pageType: PageType) =>
  pageType === 'earnings'
    ? {
        filterClicked: MIXPANEL_EVENT.earnings.campaignsTable.filterClicked,
        filterOptionClicked: MIXPANEL_EVENT.earnings.campaignsTable.filterOptionClicked,
      }
    : {
        filterClicked: MIXPANEL_EVENT.payouts.campaignsTable.filterClicked,
        filterOptionClicked: MIXPANEL_EVENT.payouts.campaignsTable.filterOptionClicked,
      };

const CampaignsCardEcho = ({ className }: { className?: string }) => {
  const { queryResult: campaigns } = useFetchQuery(useCampaignsQuery);
  const { data: campaignsData } = campaigns;
  const { page: pageType } = usePageMetadata();

  const [campaignFilterState, setCampaignFilterState] = useState<CampaignsFilter>({
    showId: [],
    campaignType: [],
    advertiserName: [],
  });

  const showOptions = useMemo(
    () =>
      campaignsData?.campaign_filters.shows.map((show) => ({ value: show.id, label: show.name })) ??
      [],
    [campaignsData]
  );

  const advertiserOptions = useMemo(
    () =>
      campaignsData?.campaign_filters.advertisers.map((name) => ({ value: name, label: name })) ??
      [],
    [campaignsData]
  );

  const campaignTypeOptions = useMemo(
    () =>
      campaignsData?.campaign_filters.campaign_types.map((type) => ({
        value: type,
        label: type,
      })) ?? [],
    [campaignsData]
  );

  const onFilterOpen = useCallback(
    (filterType: FilterType) => {
      if (!pageType) return;
      const { filterClicked } = getCampaignsFilterEventNames(pageType);
      mixpanelTrack(filterClicked, {
        widget: RevenueDashboardWidget.Campaigns,
        page: pageType,
        type: filterType,
      });
    },
    [pageType]
  );

  const onFilterChange = useCallback(
    (filterType: FilterType, ids: string[]) => {
      if (!pageType) return;
      const { filterOptionClicked } = getCampaignsFilterEventNames(pageType);
      mixpanelTrack(filterOptionClicked, {
        widget: RevenueDashboardWidget.Campaigns,
        page: pageType,
        type: filterType,
        ids,
      });
    },
    [pageType]
  );

  return (
    <CardShellEcho
      className={className}
      title="Campaigns"
      headerOptions={
        campaignsData && (
          <TableFiltersEcho
            showOptions={showOptions}
            advertiserOptions={advertiserOptions}
            campaignTypeOptions={campaignTypeOptions}
            handleSetFilterState={setCampaignFilterState}
            onFilterOpen={onFilterOpen}
            onFilterChange={onFilterChange}
          />
        )
      }
    >
      <CardContent
        {...campaigns}
        data={campaignsData}
        renderData={(data: CampaignsApiResponse) => (
          <CampaignsTableEcho data={data} filters={campaignFilterState} />
        )}
      />
    </CardShellEcho>
  );
};

export default CampaignsCardEcho;
